import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import moment from 'moment'
import { useIntl } from 'react-intl'
import { dateIsAfter, dateDifference, selectDateFormattingLocaleForRegion } from 'lib/datetime/dateUtils'
import DateInput from 'components/Common/Form/Input/DateInput'
import GeoContext from 'contexts/geoContext'

interface Props extends Partial<React.ComponentProps<typeof DateInput>> {
  travellerType?: string
  refDate?: Date
}

const MAX_SENSIBLE_AGE = 150

export function getDobPlaceholder(locale: string): string {
  // there aren't really any packages out there that we can use for this
  // so we need custom helper
  const date = new Date(Date.UTC(2006, 10, 22))
  const placeholders: Partial<Record<Intl.DateTimeFormatPartTypes, string>> = {
    day: 'DD',
    month: 'MM',
    year: 'YYYY',
  }
  // oxlint-disable-next-line datetime-display/no-inline-intl -- derives input syntax; it does not format a displayed date.
  const order = new Intl.DateTimeFormat(locale, {
    calendar: 'gregory',
    day: '2-digit',
    month: '2-digit',
    numberingSystem: 'latn',
    year: 'numeric',
    timeZone: 'UTC',
  })
    .formatToParts(date)
    .filter(({ type }) => type !== 'literal')
    .map(({ type }) => placeholders[type]!)

  return order.join('/')
}

const isFutureDate = (date: Date) => { return dateIsAfter(date, new Date()) }
const yearDifferenceFromNow = (date: Date | undefined, refDate: Date) => {
  const difference = dateDifference(refDate, date)
  return Math.round(difference.days / 365)
}
const monthDifferenceFromNow = (date: Date | undefined, refDate: Date) => {
  const difference = dateDifference(refDate, date)
  return Math.round(difference.days / 30)
}

function DateOfBirthInput(props: Props) {
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const { min, max, travellerType = 'adult', ...rest } = props
  const format = getDobPlaceholder(selectDateFormattingLocaleForRegion(currentRegionCode))

  const validationBounds = useMemo(() => {
    const refDate = props.refDate || new Date()
    return {
      upperAgeBound: yearDifferenceFromNow(min, refDate),
      lowerAgeBound: yearDifferenceFromNow(max, refDate),
      upperMonthsBound: monthDifferenceFromNow(min, refDate),
      lowerMonthsBound: monthDifferenceFromNow(max, refDate),
    }
  }, [max, min, props.refDate])

  const getInvalidMessage = useCallback((e: HTMLInputElement): string | undefined => {
    // DateInput.setCustomValidity uses plain English signals (not localized) so this comparison is safe
    if (e.validationMessage === 'Invalid date') { return e.validationMessage }
    if (e.validity.patternMismatch) { return undefined }

    const inputDateValue = moment(e.value, format).toDate()
    if (isFutureDate(inputDateValue)) {
      return intl.formatMessage({
        id: 'gLzjaEUhFtG9geYszbBr',
        defaultMessage: 'Cannot be in the future',
      })
    }

    const { upperAgeBound, lowerAgeBound, upperMonthsBound, lowerMonthsBound } = validationBounds

    let message: string | undefined
    if (e.validity.customError && e.validationMessage) {
      // Error message for traveller that is an adult or a child (with adult pricing)
      if (travellerType === 'adult' || (travellerType === 'child' && upperAgeBound === MAX_SENSIBLE_AGE)) {
        // DateInput.setCustomValidity uses plain English signals (not localized) so this comparison is safe
        const isTooYoung = e.validationMessage.startsWith('Must be before')
        message = isTooYoung
          ? intl.formatMessage(
              {
                id: '5cI3_r1V_talNdnlaZxb',
                defaultMessage: 'Must be over {age} years old',
              },
              { age: lowerAgeBound },
            )
          : intl.formatMessage(
              {
                id: 'V5oBKgoWrNS7CzJhIyl0',
                defaultMessage: 'Must be {age} years old or younger',
              },
              { age: upperAgeBound },
            )
      } else if (lowerMonthsBound > 0 && lowerMonthsBound < 12 && upperAgeBound) {
        message = intl.formatMessage(
          {
            id: 'A-2eUUT9eXiXoYH3bYsL',
            defaultMessage: 'Must be between {months, plural, one {# month} other {# months}}-{years} years old',
          },
          { months: lowerMonthsBound, years: upperAgeBound },
        )
      } else if (lowerAgeBound || upperAgeBound) {
        message = intl.formatMessage(
          {
            id: 'NZZkgTq8qxciNi2ePtTR',
            defaultMessage: 'Must be between {lower}-{upper} years old',
          },
          { lower: lowerAgeBound, upper: upperAgeBound },
        )
      } else {
        message = intl.formatMessage(
          {
            id: 'TZ-pVZ0ceG1mmcBFe3mo',
            defaultMessage: 'Must be between {lower}-{upper} months old',
          },
          { lower: lowerMonthsBound, upper: upperMonthsBound },
        )
      }
    }
    return message
  }, [format, intl, travellerType, validationBounds])

  return (
    <DateInput
      {...rest}
      min={min}
      max={max}
      displayFormat={format}
      placeholder={format}
      getInvalidMessage={getInvalidMessage}
    />
  )
}

export default DateOfBirthInput
